import React, { useState } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { EmailTag } from '../useData';
import { EMAIL_TAG_COLORS } from './EmailSidebar';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';

interface ManageTagsModalProps {
  isOpen: boolean;
  onClose: () => void;
  emailTags: EmailTag[];
  onCreateTag: (name: string, color?: string | null) => Promise<EmailTag>;
  onDeleteTag: (tagId: number) => Promise<void>;
  onReorderTags?: (tagIds: number[]) => Promise<unknown>;
  onShareTag?: (tag: EmailTag) => void;
}

const ManageTagsModal: React.FC<ManageTagsModalProps> = ({
  isOpen,
  onClose,
  emailTags,
  onCreateTag,
  onDeleteTag,
  onReorderTags,
  onShareTag,
}) => {
  const { colors } = useTheme();
  const ownTags = emailTags.filter((t) => t.isOwner !== false);
  const sharedTags = emailTags.filter((t) => t.isOwner === false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [draggedTagId, setDraggedTagId] = useState<number | null>(null);

  const handleAddTag = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newTagName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await onCreateTag(name, newTagColor);
      setNewTagName('');
      setNewTagColor(null);
    } catch {
      /**/
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const id = deleteConfirmId;
    if (id == null) return;
    setDeleteConfirmId(null);
    try {
      await onDeleteTag(id);
    } catch {
      /**/
    }
  };

  const handleReorder = (draggedId: number, targetId: number) => {
    if (!onReorderTags || draggedId === targetId) return;
    const ids = ownTags.map((t) => t.id);
    const from = ids.indexOf(draggedId);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) return;
    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, draggedId);
    onReorderTags(next);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalHeader>
          <ModalTitle>Tags</ModalTitle>
          <button
            type="button"
            onClick={onClose}
            className={cn('p-1.5 rounded-md', colors.textMuted, colors.interactiveHover)}
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-6">
            <p className={cn('text-sm', colors.textSecondary)}>
              Create tags to organize emails. Share tags with teammates so they can filter and apply the same labels.
            </p>

            {/* Add new tag form */}
            <form onSubmit={handleAddTag} className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="New tag name"
                  className={cn(
                    'flex-1 min-w-0 text-sm px-3 py-2 rounded-lg border',
                    colors.background,
                    colors.border,
                    colors.text
                  )}
                  maxLength={120}
                />
                <Button
                  type="submit"
                  disabled={saving || !newTagName.trim()}
                  className="h-12 w-12 p-0 shrink-0 flex items-center justify-center"
                  title="Add tag"
                  aria-label="Add tag"
                >
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </Button>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap" role="group" aria-label="Tag color">
                <span className={cn('text-xs font-medium', colors.textMuted)}>Color:</span>
                {EMAIL_TAG_COLORS.map((c) => (
                  <button
                    key={c ?? 'none'}
                    type="button"
                    onClick={() => setNewTagColor(c)}
                    title={c ? `Color ${c}` : 'No color'}
                    className={cn(
                      'w-5 h-5 rounded-full border-2 flex-shrink-0',
                      newTagColor === c ? 'border-gray-900 dark:border-white ring-1 ring-offset-1 ring-gray-400 dark:ring-gray-500' : 'border-transparent',
                      colors.interactiveHover
                    )}
                    style={c ? { backgroundColor: c } : { backgroundColor: '#9ca3af' }}
                  />
                ))}
              </div>
            </form>

            {/* List of existing tags */}
            <div>
              <h3 className={cn('text-sm font-semibold mb-3', colors.text)}>Your tags</h3>
              {ownTags.length === 0 ? (
                <div
                  className={cn(
                    'rounded-xl border-2 border-dashed py-8 px-4 text-center',
                    colors.border,
                    colors.surfaceHover
                  )}
                >
                  <p className={cn('text-sm', colors.textMuted)}>No tags yet. Add one above.</p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {ownTags.map((tag) => (
                    <li
                      key={tag.id}
                      draggable={!!onReorderTags}
                      onDragStart={(e) => {
                        if (!onReorderTags) return;
                        e.dataTransfer.setData('text/plain', String(tag.id));
                        e.dataTransfer.effectAllowed = 'move';
                        setDraggedTagId(tag.id);
                      }}
                      onDragEnd={() => setDraggedTagId(null)}
                      onDragOver={(e) => {
                        if (!onReorderTags) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const raw = e.dataTransfer.getData('text/plain');
                        const draggedId = raw ? parseInt(raw, 10) : NaN;
                        if (!Number.isNaN(draggedId) && draggedId !== tag.id) handleReorder(draggedId, tag.id);
                        setDraggedTagId(null);
                      }}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border px-4 py-3 transition-colors',
                        colors.border,
                        colors.surface,
                        'hover:shadow-sm',
                        onReorderTags && 'cursor-grab active:cursor-grabbing',
                        draggedTagId === tag.id && 'opacity-50 bg-gray-100 dark:bg-gray-800/50'
                      )}
                    >
                      {onReorderTags && (
                        <span
                          className={cn('flex-shrink-0 text-gray-400 dark:text-gray-500 pointer-events-none')}
                          title="Drag to reorder"
                          aria-hidden
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 6h2v2H8V6zm0 5h2v2H8v-2zm0 5h2v2H8v-2zm5-10h2v2h-2V6zm0 5h2v2h-2v-2zm0 5h2v2h-2v-2z" />
                          </svg>
                        </span>
                      )}
                      <span
                        className={cn('w-3 h-3 rounded-full flex-shrink-0', !tag.color && 'bg-gray-300 dark:bg-gray-600')}
                        style={tag.color ? { backgroundColor: tag.color } : undefined}
                        aria-hidden
                      />
                      <span className={cn('flex-1 min-w-0 truncate font-medium', colors.text)}>{tag.name}</span>
                      <span className={cn('text-sm tabular-nums flex-shrink-0', colors.textMuted)}>
                        {tag._count?.assignments ?? 0}
                      </span>
                      {onShareTag && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onShareTag(tag)}
                          className="h-10 w-10 p-0 shrink-0"
                          title="Share tag"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                          </svg>
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirmId(tag.id)}
                        className="h-10 w-10 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 shrink-0"
                        title="Delete tag"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {sharedTags.length > 0 && (
              <div>
                <h3 className={cn('text-sm font-semibold mb-3', colors.text)}>Shared with you</h3>
                <ul className="space-y-2">
                  {sharedTags.map((tag) => (
                    <li
                      key={tag.id}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border px-4 py-3',
                        colors.border,
                        colors.surface,
                      )}
                    >
                      <span
                        className={cn('w-3 h-3 rounded-full flex-shrink-0', !tag.color && 'bg-gray-300 dark:bg-gray-600')}
                        style={tag.color ? { backgroundColor: tag.color } : undefined}
                      />
                      <div className="min-w-0 flex-1">
                        <span className={cn('block truncate font-medium', colors.text)}>{tag.name}</span>
                        {tag.ownerName && (
                          <span className={cn('block text-xs truncate', colors.textMuted)}>From {tag.ownerName}</span>
                        )}
                      </div>
                      <span className={cn('text-xs shrink-0', colors.textMuted)}>
                        {tag.permission === 'write' ? 'Can apply' : 'View only'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </ModalContent>
      </Modal>

      <ConfirmationDialog
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete tag"
        message="Are you sure you want to delete this tag? It will be removed from all emails."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </>
  );
};

export default ManageTagsModal;
